import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { authLogger } from '@/utils/authLogger';

export type AppRole = 
  | 'super_admin' 
  | 'admin' 
  | 'it_admin' 
  | 'platform_admin' 
  | 'marketing' 
  | 'product_manager' 
  | 'intern'
  | 'moderator'
  | 'user';

interface UseUserRolesResult {
  roles: AppRole[];
  isLoading: boolean;
  hasElevatedAccess: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  activeRole: AppRole | null;
  setActiveRole: (role: AppRole) => void;
  hasRole: (role: AppRole) => boolean;
  hasPermission: (platform: string, permission: string) => Promise<boolean>;
  hasPermissionSync: (platform: string, permission: string) => boolean;
  refetch: () => Promise<void>;
}

interface PermissionCacheEntry {
  value: boolean;
  timestamp: number;
}

const ELEVATED_ROLES: AppRole[] = ['super_admin', 'admin', 'it_admin', 'platform_admin', 'marketing', 'product_manager', 'intern'];
const PERMISSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useUserRoles = (profileId?: string): UseUserRolesResult => {
  // Fallback to cached profileId from localStorage if not provided
  const effectiveProfileId = profileId || localStorage.getItem('sirppi_profile_id') || undefined;
  
  const [roles, setRoles] = useState<AppRole[]>([]);
  // Start with true only if effectiveProfileId is provided; otherwise false (nothing to load)
  const [isLoading, setIsLoading] = useState(!!effectiveProfileId);
  const [activeRole, setActiveRoleState] = useState<AppRole | null>(null);
  const [permissionsCache, setPermissionsCache] = useState<Map<string, PermissionCacheEntry>>(new Map());
  const [isCacheLoading, setIsCacheLoading] = useState(false);
  const lastProfileId = useRef<string | null>(null);

  // Fetch and cache all permissions for the user
  const loadPermissionsCache = useCallback(async () => {
    if (!effectiveProfileId || roles.length === 0) return;
    
    // Super admin and admin have full access - no need to cache
    if (roles.includes('super_admin') || roles.includes('admin')) {
      return;
    }

    setIsCacheLoading(true);
    try {
      const platforms = ['all', 'analytics', 'beta', 'careers', 'content', 'features', 
                         'insights', 'marketing', 'overview', 'revenue', 'security', 
                         'settings', 'solomon', 'support', 'system', 'trends', 'users'];
      const permissions = ['read', 'analytics_only', 'marketing_only', 'manage', 'full_access'];
      
      const cache = new Map<string, PermissionCacheEntry>();
      const timestamp = Date.now();

      // Batch check permissions using database function
      for (const platform of platforms) {
        for (const perm of permissions) {
          const { data, error } = await (supabase as any).rpc('check_role_permission', {
            user_id_input: effectiveProfileId,
            platform_input: platform,
            permission_input: perm
          });
          
          if (!error) {
            cache.set(`${platform}:${perm}`, { value: data === true, timestamp });
          }
        }
      }
      
      setPermissionsCache(cache);
    } catch (err) {
      console.error('[useUserRoles] Error loading permissions cache:', err);
    } finally {
      setIsCacheLoading(false);
    }
  }, [effectiveProfileId, roles]);

  const fetchRoles = useCallback(async () => {
    if (!effectiveProfileId) {
      authLogger.debug('[useUserRoles] No profileId (checked localStorage fallback), clearing roles');
      setRoles([]);
      setIsLoading(false);
      return;
    }

    // Check for recently cached roles in localStorage (set after OTP verification)
    const cachedRoles = localStorage.getItem('user_roles');
    const rolesFetchedAt = localStorage.getItem('roles_fetched_at');
    const isCacheFresh = rolesFetchedAt && 
      (Date.now() - parseInt(rolesFetchedAt, 10)) < 60000; // 1 minute cache

    // SKIP CACHE during admin navigation grace period (forces fresh fetch)
    const adminNavGrace = localStorage.getItem('admin_navigation_grace');
    const isAdminNavigation = adminNavGrace && (Date.now() - parseInt(adminNavGrace, 10)) < 5000;
    
    if (isAdminNavigation) {
      authLogger.debug('[useUserRoles] Skipping cache due to admin navigation grace period');
      // Clear the grace flag after using it once
      localStorage.removeItem('admin_navigation_grace');
    }

    if (cachedRoles && isCacheFresh && !isAdminNavigation) {
      try {
        const parsedRoles = JSON.parse(cachedRoles) as AppRole[];
        authLogger.debug('[useUserRoles] Using localStorage cached roles:', { roles: parsedRoles });
        setRoles(parsedRoles);
        
        // Set active role from localStorage or use first elevated role
        const storedRole = localStorage.getItem('active_admin_role') as AppRole | null;
        if (storedRole && parsedRoles.includes(storedRole)) {
          setActiveRoleState(storedRole);
        } else if (parsedRoles.length > 0) {
          const firstElevated = parsedRoles.find(r => ELEVATED_ROLES.includes(r));
          setActiveRoleState(firstElevated || parsedRoles[0]);
        }
        
        setIsLoading(false);
        return;
      } catch (e) {
        authLogger.debug('[useUserRoles] Failed to parse cached roles, fetching from API');
      }
    }

    // Get token from localStorage for edge function auth
    // Check both possible token keys (admin_auth_token has priority for backward compatibility)
    const token = localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token');
    if (!token) {
      authLogger.debug('[useUserRoles] No auth token found, clearing roles');
      setRoles([]);
      setIsLoading(false);
      return;
    }

    authLogger.rolesFetchStart(effectiveProfileId);
    setIsLoading(true);
    try {
      // Use edge function to fetch roles (bypasses RLS auth.uid() issue)
      const { data, error } = await supabase.functions.invoke('get-user-roles', {
        body: { token, profileId: effectiveProfileId }
      });

      if (error) {
        authLogger.rolesFetchFailed(effectiveProfileId, error.message);
        console.error('[useUserRoles] Edge function error:', error);
        setRoles([]);
      } else if (data?.error) {
        authLogger.rolesFetchFailed(effectiveProfileId, data.error);
        console.error('[useUserRoles] API error:', data.error);
        setRoles([]);
      } else {
        const userRoles = (data?.roles as AppRole[]) || [];
        const hasAdmin = userRoles.includes('admin') || userRoles.includes('super_admin');
        authLogger.rolesFetchSuccess(effectiveProfileId, userRoles, hasAdmin);
        console.log('[useUserRoles] Roles fetched successfully:', userRoles);
        setRoles(userRoles);

        // Self-heal stale cached profileId if the server resolved a different one
        // (happens when localStorage holds a profileId from a previous session)
        if (data?.profileId && data.profileId !== effectiveProfileId) {
          console.log('[useUserRoles] Self-healing stale profileId cache', {
            stale: effectiveProfileId,
            resolved: data.profileId,
          });
          localStorage.setItem('sirppi_profile_id', data.profileId);
        }

        // Cache roles in localStorage for stale-while-revalidate pattern
        localStorage.setItem('user_roles', JSON.stringify(userRoles));
        localStorage.setItem('roles_fetched_at', Date.now().toString());
        
        // Set active role from localStorage or use first elevated role
        const storedRole = localStorage.getItem('active_admin_role') as AppRole | null;
        if (storedRole && userRoles.includes(storedRole)) {
          setActiveRoleState(storedRole);
        } else if (userRoles.length > 0) {
          const firstElevated = userRoles.find(r => ELEVATED_ROLES.includes(r));
          setActiveRoleState(firstElevated || userRoles[0]);
        }
      }
    } catch (err) {
      authLogger.rolesFetchFailed(effectiveProfileId, String(err));
      console.error('[useUserRoles] Exception:', err);
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveProfileId]);

  // Refetch when effectiveProfileId changes from undefined to a value
  useEffect(() => {
    if (effectiveProfileId && effectiveProfileId !== lastProfileId.current) {
      authLogger.debug('[useUserRoles] ProfileId changed, refetching', { 
        profileId: effectiveProfileId,
        source: profileId ? 'prop' : 'localStorage'
      });
      lastProfileId.current = effectiveProfileId;
      fetchRoles();
    } else if (!effectiveProfileId && lastProfileId.current) {
      authLogger.debug('[useUserRoles] ProfileId cleared');
      lastProfileId.current = null;
      setRoles([]);
      setIsLoading(false);
    }
  }, [effectiveProfileId, profileId, fetchRoles]);

  // Load permissions cache when roles change
  useEffect(() => {
    if (!isLoading && roles.length > 0) {
      loadPermissionsCache();
    }
  }, [isLoading, roles, loadPermissionsCache]);

  const setActiveRole = useCallback((role: AppRole) => {
    setActiveRoleState(role);
    localStorage.setItem('active_admin_role', role);
    // Clear permissions cache to force refresh
    setPermissionsCache(new Map());
  }, []);

  const hasRole = useCallback((role: AppRole): boolean => {
    return roles.includes(role);
  }, [roles]);

  // Async permission check using database function
  const hasPermission = useCallback(async (platform: string, permission: string): Promise<boolean> => {
    // Super admin and admin have full access
    if (roles.includes('super_admin') || roles.includes('admin')) {
      return true;
    }
    
    if (!effectiveProfileId) return false;
    
    try {
      const { data, error } = await (supabase as any).rpc('check_role_permission', {
        user_id_input: effectiveProfileId,
        platform_input: platform,
        permission_input: permission
      });
      
      if (error) {
        console.error('[useUserRoles] Error checking permission:', error);
        return false;
      }
      
      return data === true;
    } catch (err) {
      console.error('[useUserRoles] Exception checking permission:', err);
      return false;
    }
  }, [roles, effectiveProfileId]);

  // Synchronous permission check using cached data from database
  const hasPermissionSync = useCallback((platform: string, permission: string): boolean => {
    // Super admin and admin have full access - this is the ONLY hardcoded check allowed
    if (roles.includes('super_admin') || roles.includes('admin')) {
      return true;
    }
    
    // Check cache
    const cacheKey = `${platform}:${permission}`;
    const cached = permissionsCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < PERMISSION_CACHE_TTL) {
      return cached.value;
    }
    
    // If cache is loading or empty, be permissive temporarily to avoid UI flicker
    // The actual permission will be enforced once cache loads
    if (isCacheLoading || permissionsCache.size === 0) {
      return false; // Default to denied while loading
    }
    
    return false;
  }, [roles, permissionsCache, isCacheLoading]);

  const hasElevatedAccess = roles.some(role => ELEVATED_ROLES.includes(role));
  const isSuperAdmin = roles.includes('super_admin');
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');

  return {
    roles,
    isLoading,
    hasElevatedAccess,
    isSuperAdmin,
    isAdmin,
    activeRole,
    setActiveRole,
    hasRole,
    hasPermission,
    hasPermissionSync,
    refetch: fetchRoles
  };
};
